/**
 * The nine sections after the hero.
 *
 * One file rather than nine, because each is now three lines: `Section` carries
 * the eyebrow/title/claim/figure/proof shape, the figures carry themselves, and
 * the copy lives in the catalogue. A file per section would be nine imports and
 * nine identical wrappers around one component call.
 */
import { Section, Body, Pull, FigureFrame } from './Section'
import { useT } from '../i18n/context'

/**
 * Every band takes the number it holds on the page that renders it.
 *
 * The number is the page's, not the band's: `pools` is 01 on the product page
 * and nothing at all on a page that shows it alone. So it is a prop rather than
 * a constant here — a band does not know how many of its neighbours a reader is
 * about to scroll through, and that is precisely what the number tells them.
 */
interface BandProps {
  index?: number
}

import { PoolLattice } from '../figures/pools/PoolLattice'
import { DecisionLedger } from '../figures/decision/DecisionLedger'
import { TerminalWall } from '../figures/exec/TerminalWall'
import { EvidenceChain } from '../figures/evidence/EvidenceChain'
import { ApprovalGate } from '../figures/approval/ApprovalGate'
import { FenceTimeline, SafeBoundaryStrip } from '../figures/continuity/FenceTimeline'
import { FleetBoard, DegradationTable } from '../figures/fleet/FleetBoard'
import { TrustBoundary } from '../figures/security/TrustBoundary'
import { InstallTerminal, HonestGaps } from '../figures/install/InstallTerminal'

export function Pools({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="pools"
      index={index}
      eyebrow="pools.eyebrow"
      title="pools.title"
      sub="pools.sub"
      proof="pools.proof"
      tone="sunken"
      figure={
        <FigureFrame alt={t('pools.fig.alt')}>
          <PoolLattice />
        </FigureFrame>
      }
    >
      <Body>{t('pools.body.1')}</Body>
      <Pull>{t('pools.pull')}</Pull>
      <Body>{t('pools.body.2')}</Body>
    </Section>
  )
}

export function Decision({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="decision"
      index={index}
      eyebrow="decision.eyebrow"
      title="decision.title"
      sub="decision.sub"
      proof="decision.proof"
      figure={
        <FigureFrame alt={t('decision.fig.alt')}>
          <DecisionLedger />
        </FigureFrame>
      }
    >
      <Body>{t('decision.body.1')}</Body>
      <Pull>{t('decision.pull')}</Pull>
      <Body>{t('decision.body.2')}</Body>
    </Section>
  )
}

export function Execution({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="execution"
      index={index}
      eyebrow="exec.eyebrow"
      title="exec.title"
      sub="exec.sub"
      proof="exec.proof"
      tone="sunken"
      figure={
        <FigureFrame alt={t('exec.fig.alt')}>
          <TerminalWall />
        </FigureFrame>
      }
    >
      <Body>{t('exec.body.1')}</Body>
      <Body>{t('exec.body.2')}</Body>
    </Section>
  )
}

export function Evidence({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="evidence"
      index={index}
      eyebrow="evidence.eyebrow"
      title="evidence.title"
      sub="evidence.sub"
      proof="evidence.proof"
      figure={
        <FigureFrame alt={t('evidence.fig.alt')}>
          <EvidenceChain />
        </FigureFrame>
      }
    >
      <Body>{t('evidence.body.1')}</Body>
      <Pull>{t('evidence.pull')}</Pull>
      <Body>{t('evidence.body.2')}</Body>
    </Section>
  )
}

export function Approval({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="approval"
      index={index}
      eyebrow="approval.eyebrow"
      title="approval.title"
      sub="approval.sub"
      proof="approval.proof"
      tone="sunken"
      figure={
        <FigureFrame alt={t('approval.fig.alt')}>
          <ApprovalGate />
        </FigureFrame>
      }
    >
      <Body>{t('approval.body.1')}</Body>
    </Section>
  )
}

export function Continuity({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="continuity"
      index={index}
      eyebrow="continuity.eyebrow"
      title="continuity.title"
      sub="continuity.sub"
      proof="continuity.proof"
      figure={
        <FigureFrame alt={t('continuity.fig.alt')}>
          <FenceTimeline />
          <SafeBoundaryStrip />
        </FigureFrame>
      }
    >
      <Body>{t('continuity.body.1')}</Body>
      <Pull>{t('continuity.pull')}</Pull>
      <Body>{t('continuity.body.2')}</Body>
    </Section>
  )
}

export function Fleet({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="fleet"
      index={index}
      eyebrow="fleet.eyebrow"
      title="fleet.title"
      sub="fleet.sub"
      proof="fleet.proof"
      tone="sunken"
      figure={
        <FigureFrame alt={t('fleet.fig.alt')}>
          <FleetBoard />
          <DegradationTable />
        </FigureFrame>
      }
    >
      <Body>{t('fleet.body.1')}</Body>
    </Section>
  )
}

export function Security({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="security"
      index={index}
      eyebrow="security.eyebrow"
      title="security.title"
      sub="security.sub"
      proof="security.proof"
      figure={
        <FigureFrame alt={t('security.fig.alt')}>
          <TrustBoundary />
        </FigureFrame>
      }
    >
      <Body>{t('security.body.1')}</Body>
      <Pull>{t('security.pull')}</Pull>
      <Body>{t('security.body.2')}</Body>
    </Section>
  )
}

export function Install({ index }: BandProps) {
  const t = useT()
  return (
    <Section
      id="install"
      index={index}
      eyebrow="install.eyebrow"
      title="install.title"
      sub="install.sub"
      proof="install.proof"
      tone="sunken"
      // No FigureFrame here: this figure is already text. A `figcaption`
      // describing a terminal transcript to somebody whose screen reader is
      // about to read them the transcript is noise, and an empty one is worse.
      figure={
        <>
          <InstallTerminal />
          <HonestGaps />
        </>
      }
    >
      <Body>{t('install.body.1')}</Body>
    </Section>
  )
}
